import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import {
  ICreateWorkOrderRequest,
  IWorkOrder,
  WorkOrderItemType,
  WorkOrderPriority,
  WorkOrderStatus,
} from "../../models/WorkOrder";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { DateTimeInput } from "../shared/Inputs/DateTimeInput";
import SearchPaletteMultiSelectInput from "../shared/Inputs/SearchPaletteMultiSelectInput";
import { SearchPaletteItem, SearchPaletteItemType } from "../shared/Inputs/SearchPaletteTypes";
import SelectInputWithDot from "../shared/Inputs/SelectInputWithDot";
import { TextInput } from "../shared/Inputs/TextInput";
import WilmaUserSelect from "../shared/WilmaUsers/WilmaUserSelect";

interface WorkOrderFormValues {
  title: string;
  notes: string;
  priority: WorkOrderPriority;
  assigneeId: string | null;
  dueDate: string;
  reminderDate: string;
}

interface WorkOrderFormProps {
  workOrder?: IWorkOrder | null;
  initialLinkedItems: SearchPaletteItem[];
  createdById?: string | null;
  onCancel: () => void;
  onSubmit: (request: ICreateWorkOrderRequest) => void;
  isSubmitting?: boolean;
  onComplete: () => void;
}

export default function WorkOrderForm({
  workOrder,
  initialLinkedItems,
  createdById = null,
  onCancel,
  onSubmit,
  isSubmitting = false,
  onComplete
}: WorkOrderFormProps) {
  const isEditMode = Boolean(workOrder);
  const allowedLinkedItemTypes: SearchPaletteItemType[] = ["Order", "Vehicle", "Stock"];

  const [selectedItems, setSelectedItems] = useState<SearchPaletteItem[]>([]);

  useEffect(() => {
    if (!isEditMode) {
      setSelectedItems(initialLinkedItems);
      return;
    }

    if (!workOrder?.workOrderItems?.length) {
      setSelectedItems([]);
      return;
    }

    const mappedItems: SearchPaletteItem[] = workOrder.workOrderItems.map((item) => ({
      id: item.linkedId,
      name: item.title,
      type: mapWorkOrderItemTypeToSearchType(item.type),
    }));

    setSelectedItems(mergeWithNewlyAddedItems(mappedItems, initialLinkedItems));
  }, [workOrder, initialLinkedItems, isEditMode]);

  const defaultValues = useMemo<WorkOrderFormValues>(
    () => ({
      title: workOrder?.title ?? "",
      notes: workOrder?.notes ?? "",
      priority: workOrder?.priority ?? WorkOrderPriority.Medium,
      assigneeId: workOrder?.assigneeId ?? null,
      dueDate: workOrder?.dueDate ? toDateTimeLocalValue(workOrder.dueDate) : "",
      reminderDate: workOrder?.reminderDate ? toDateTimeLocalValue(workOrder.reminderDate) : "",
    }),
    [workOrder]
  );

  const { register, handleSubmit, setValue, watch, reset } = useForm<WorkOrderFormValues>({
    defaultValues,
    values: defaultValues,
  });

  const priorityOptions = useMemo(
    () =>
      Object.values(WorkOrderPriority)
        .filter((x) => isNaN(Number(x)))
        .map((value) => ({
          id: WorkOrderPriority[value as keyof typeof WorkOrderPriority],
          name: value as string,
          dotClassName: getPriorityDotClass(WorkOrderPriority[value as keyof typeof WorkOrderPriority]),
        })),
    []
  );

  const handleFormSubmit = (data: WorkOrderFormValues) => {
    const itemSource = selectedItems.length > 0
      ? selectedItems.map((item) => ({
        type: mapSearchTypeToWorkOrderItemType(item.type),
        title: item.name,
        linkedId: String(item.id),
      }))
      : (workOrder?.workOrderItems ?? []).map((item) => ({
        type: item.type,
        title: item.title,
        linkedId: item.linkedId,
      }));

    const request: ICreateWorkOrderRequest = {
      title: data.title,
      notes: data.notes,
      assigneeId: data.assigneeId ?? workOrder?.assigneeId ?? null,
      createdById: workOrder?.createdById ?? createdById,
      priority: data.priority,
      status: workOrder?.status ?? WorkOrderStatus.ToDo,
      dueDate: data.dueDate ? new Date(data.dueDate).toISOString() : null,
      reminderDate: data.reminderDate ? new Date(data.reminderDate).toISOString() : null,
      items: itemSource,
    };

    // clear form
    reset(defaultValues);
    onSubmit(request);
    onComplete();
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="p-4">
      <h3 className="text-lg font-semibold text-gray-900">
        {isEditMode ? "Edit Work Order" : "Create Work Order"}
      </h3>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="sm:col-span-4">
          <TextInput
            label="Title"
            register={register}
            registerName="title"
            placeholder="What needs to be done?"
            required
            rounded
          />
        </div>
        {/* ======================= */}
        <SelectInputWithDot
          label="Priority"
          register={register}
          registerName="priority"
          options={priorityOptions}
          setValue={setValue}
          watch={watch}
          rounded
        />

        <WilmaUserSelect
          label="Assignee"
          register={register}
          registerName="assigneeId" 
          value={""} 
          setValue={setValue} watch={watch}          
        />

        <DateTimeInput
          label="Due Date"
          register={register}
          registerName="dueDate"
        />

        <DateTimeInput
          label="Reminder Date"
          register={register}
          registerName="reminderDate"
        />
        {/* ======================= */}
        <div className="sm:col-span-4">
          <TextInput
            label="Notes"
            register={register}
            registerName="notes"
            placeholder="Additional notes..."
            richText
            rounded
          />
        </div>
        {/* ======================= */}
        <div className="sm:col-span-4">
          <SearchPaletteMultiSelectInput
            label="Linked Items"
            placeholder="Search items..."
            theme="light"
            allowedTypes={allowedLinkedItemTypes}
            selectedItems={selectedItems}
            onChange={(items) => {
              setSelectedItems(items);
            }}
          />
        </div>
        
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <ButtonInput
          label="Cancel"
          isSubmit={false}
          classes="!bg-gray-200 !text-gray-700 hover:!bg-gray-300"
          onClick={onCancel}
        />
        <ButtonInput
          label={isEditMode ? "Save Order" : "Create Order"}
          isSubmit={true}
          isLoading={isSubmitting}
          classes=""
          onClick={undefined}
        />
      </div>
    </form>
  );
}

function toDateTimeLocalValue(dateValue: string): string {
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

function getPriorityDotClass(priority: WorkOrderPriority): string {
  switch (priority) {
    case WorkOrderPriority.Low:
      return "bg-green-500";
    case WorkOrderPriority.Medium:
      return "bg-yellow-500";
    case WorkOrderPriority.High:
      return "bg-orange-500";
    case WorkOrderPriority.Critical:
      return "bg-red-500";
    default:
      return "bg-gray-400";
  }
}

function mapSearchTypeToWorkOrderItemType(type?: SearchPaletteItem["type"]): WorkOrderItemType {
  switch (type) {
    case "Order":
      return WorkOrderItemType.Order;
    case "Vehicle":
      return WorkOrderItemType.Vehicle;
    case "Stock":
      return WorkOrderItemType.Stock;
    default:
      return WorkOrderItemType.Vehicle;
  }
}

function mapWorkOrderItemTypeToSearchType(type: WorkOrderItemType): SearchPaletteItem["type"] {
  switch (type) {
    case WorkOrderItemType.Order:
      return "Order";
    case WorkOrderItemType.Vehicle:
      return "Vehicle";
    case WorkOrderItemType.Lead:
      return "Lead";
    case WorkOrderItemType.Stock:
      return "Stock";
    default:
      return "Vehicle";
  }
}

function mergeWithNewlyAddedItems(
  existingItems: SearchPaletteItem[],
  itemsToAdd: SearchPaletteItem[]
): SearchPaletteItem[] {
  if (!itemsToAdd.length) return existingItems;

  const existingKeys = new Set(
    existingItems.map((item) => `${String(item.id)}::${item.type ?? "Vehicle"}`)
  );

  const additions = itemsToAdd
    .filter((item) => !existingKeys.has(`${String(item.id)}::${item.type ?? "Vehicle"}`))
    .map((item) => ({
      ...item,
      isNewlyAdded: true,
    }));

  return [...existingItems, ...additions];
}
